import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import async from 'async';
import pluralize from 'pluralize';
import { SOLR_DELAY } from '../../../../constants';
import {
  getUrlRulesets,
  addUrlRuleset,
  deleteUrlRuleset,
  duplicateUrlRuleset,
  getInUse,
  select,
  unselectAll,
} from '../../../actions/urlRulesetActions';
import { addNotification } from '../../../actions/notificationActions';
import Spinner from '../../../components/Spinner/Spinner';
import Confirm from '../../../components/Confirm/Confirm';
import UrlRulesetsTable from '../../../components/UrlRulesets/UrlRulesetsTable';
import AddUrlRulesetDialog from '../../../components/UrlRulesets/AddUrlRulesetDialog';
import DuplicateUrlRulesetDialog from '../../../components/UrlRulesets/DuplicateUrlRulesetDialog';
import ErrorPage from '../ErrorPage/ErrorPage';
import errorMessage from '../../../utils/errorMessage';
import Header from '../../../components/Header/Header';
import URLRulesetsActions from './URLRulesetsActions';

export class UrlRulesetsPage extends Component {
  state = {
    showAddDialog: false,
    showDuplicateDialog: false,
  };

  componentDidMount() {
    this.loadData();
    document.addEventListener('visibilitychange', this.loadData);
  }

  componentWillUnmount() {
    const { unselectAll } = this.props;

    unselectAll();
    document.removeEventListener('visibilitychange', this.loadData);
  }

  loadData = () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    const { getUrlRulesets, getInUse } = this.props;

    getUrlRulesets();
    getInUse();
  };

  getSelectedItems = () => {
    const {
      urlRulesets: { urlRulesets, selected },
    } = this.props;

    return Object.keys(selected).map(index => urlRulesets[index]);
  };

  getSelectedExceptInUse = () => {
    const {
      urlRulesets: { inUse },
    } = this.props;

    return this.getSelectedItems().filter(
      item => !inUse.some(inuse => +inuse.urlRulesetId === +item.id),
    );
  };

  add = name => {
    const { getUrlRulesets, addNotification } = this.props;

    return addUrlRuleset(name).then(
      res => {
        if (res.id > -1) {
          addNotification({ type: 'success', text: 'URL Ruleset added.' });
          setTimeout(getUrlRulesets, SOLR_DELAY);
        } else {
          addNotification({ type: 'danger', text: 'Request failed.' });
        }
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  duplicate = name => {
    const { getUrlRulesets, addNotification } = this.props;
    const { id } = this.getSelectedItems()[0];

    return duplicateUrlRuleset(id, name).then(
      res => {
        if (res.id > 0) {
          addNotification({ type: 'success', text: 'URL Ruleset duplicated.' });
          setTimeout(getUrlRulesets, SOLR_DELAY);
        } else {
          addNotification({ type: 'danger', text: 'Request failed.' });
        }
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  delete = () => {
    const items = this.getSelectedExceptInUse();

    async.filter(
      items,
      (item, callback) =>
        deleteUrlRuleset(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      this.deleteCallback,
    );
  };

  deleteCallback = (err, results) => {
    const { getUrlRulesets, addNotification, unselectAll } = this.props;
    const items = this.getSelectedExceptInUse();
    let failedItems;
    let errorMsg = '';

    // check for errors
    if (items.length === results.length) {
      addNotification({
        type: 'success',
        text: `${pluralize('URL Ruleset', items.length, true)} deleted.`,
      });
    } else {
      // collect the failed request items
      failedItems = items.filter(item => !results.includes(item));
      failedItems.forEach(item => {
        errorMsg += (errorMsg ? ', ' : 'Failed to delete: ') + item.name;
      });
      addNotification({ type: 'danger', text: errorMsg });
    }

    // if there was at least one successful request, update
    if (results.length) {
      unselectAll();
      setTimeout(getUrlRulesets, SOLR_DELAY);
    }
  };

  successAddDialog = urlRuleset => {
    this.hideAddDialog();
    this.add(urlRuleset);
  };

  successDuplicateDialog = urlRuleset => {
    this.hideDuplicateDialog();
    this.duplicate(urlRuleset);
  };

  hideAddDialog = () => this.setState({ showAddDialog: false });

  hideDuplicateDialog = () => this.setState({ showDuplicateDialog: false });

  handleClickAdd = () => {
    this.setState({ showAddDialog: true });
  };

  handleClickDuplicate = () => {
    this.setState({ showDuplicateDialog: true });
  };

  handleClickDelete = () => {
    const confirm = this.refDelete;
    const items = this.getSelectedExceptInUse();
    const names = items.map((item, index) => <li key={index}>{item.name}</li>);
    const title = `Delete ${pluralize('URL Ruleset', items.length, true)}`;
    const body = (
      <div>
        <p>
          Are you sure you want to delete {pluralize('this', items.length)}{' '}
          {pluralize('URL Ruleset', items.length)}?
        </p>
        <ol>{names}</ol>
      </div>
    );

    confirm.show(title, body);
    confirm.onSuccess(this.delete);

    return { title, body };
  };

  handleClickTable = urlRulesets => {
    const { select } = this.props;

    select(urlRulesets);
  };

  handleDoubleClickTable = (event, urlRuleset) => {
    const { history } = this.props;

    history.push(`/url-rulesets/${urlRuleset.id}`);
  };

  render() {
    const {
      urlRulesets: { urlRulesets, selected, loading, inUse, error },
      addNotification,
    } = this.props;
    const { showAddDialog, showDuplicateDialog } = this.state;
    const items = this.getSelectedItems();

    if (error) {
      return <ErrorPage error={error} />;
    }

    const removables = this.getSelectedExceptInUse().length;

    return (
      <div className="page-structure !bg-star-light min-h-screen">
        <Helmet title="URL Rulesets" />
        <Header />
        <URLRulesetsActions
          removables={removables}
          handleClickAdd={this.handleClickAdd}
          getSelectedExceptInUse={this.getSelectedExceptInUse}
          getSelectedItems={this.getSelectedItems}
          handleClickDelete={this.handleClickDelete}
          handleClickDuplicate={this.handleClickDuplicate}
        />
        <div className="mt-8 !bg-star-light">
          <div className="page-content page-url-rulesets">
            <Spinner loading={loading}>
              <UrlRulesetsTable
                data={urlRulesets}
                inUse={inUse}
                selected={selected}
                onClick={this.handleClickTable}
                onDoubleClick={this.handleDoubleClickTable}
              />
            </Spinner>
          </div>
        </div>
        <AddUrlRulesetDialog
          show={showAddDialog}
          onHide={this.hideAddDialog}
          onSuccess={this.successAddDialog}
          urlRulesets={urlRulesets}
          addNotification={addNotification}
        />
        <DuplicateUrlRulesetDialog
          show={showDuplicateDialog}
          onHide={this.hideDuplicateDialog}
          onSuccess={this.successDuplicateDialog}
          urlRulesets={urlRulesets}
          original={items[0]}
          addNotification={addNotification}
        />
        <Confirm
          ref={ref => {
            this.refDelete = ref;
          }}
        />
      </div>
    );
  }
}

UrlRulesetsPage.propTypes = {
  history: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  urlRulesets: PropTypes.object.isRequired,
  getUrlRulesets: PropTypes.func.isRequired,
  getInUse: PropTypes.func.isRequired,
  select: PropTypes.func.isRequired,
  unselectAll: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  urlRulesets: state.urlRulesets,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getUrlRulesets,
      getInUse,
      select,
      unselectAll,
      addNotification,
    },
    dispatch,
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UrlRulesetsPage));
